import { buildGenerationTargetCacheKey } from '@/lib/generation/generation-reference';
import { buildGenerationReferenceIdentity } from '@/lib/generation/generation-reference-draft-reconcile';
import {
  formatCanvasVersionedDisplayTitle,
  splitCanvasDisplayNameAndVersion,
} from '@/lib/canvas/canvas-versioned-display-title';
import {
  GenerationResourceType,
  type GenerationReferenceDraft,
  type GenerationReferenceItem,
  type GenerationResourceTypeValue,
  type GenerationTargetKey,
} from '@/types/generation-reference';
import type {
  CanvasAttachmentSnapshot,
  CanvasConversationSummary,
  CanvasGenerationTargetSnapshot,
  CanvasHistoryItem,
  CanvasProjectedResource,
  CanvasReferenceSource,
  CanvasTargetHistory,
} from '@/types/canvas-snapshot';
import { isFootageMarkupLineageAction } from '@/types/edit-image';
import { isCanvasPinAttachmentIdentity } from '@/lib/canvas/canvas-markup-reference-pin';
import { isCanvasResourcePinItemType } from '@/lib/canvas/canvas-layout-item-key';

export type CanvasReferenceProjectionNodeKind =
  | 'reference'
  | 'attachment'
  | 'history_group'
  | 'history'
  | 'conversation';

/** provenance 路径 ensureNode(kind:reference) 的可序列化标记（D1 按选中门控隐藏）。 */
export const CANVAS_HISTORY_PROVENANCE_CREATED_BY = 'history-provenance' as const;
export type CanvasReferenceProjectionCreatedBy = typeof CANVAS_HISTORY_PROVENANCE_CREATED_BY;

/** 历史版本组 id 前缀：后缀为绑定的根画布节点 id（视频=video 节点，图=plan 节点）。 */
export const CANVAS_HISTORY_GROUP_ID_PREFIX = 'canvas-history-group:';

export function buildCanvasHistoryGroupId(anchorNodeId: string): string {
  return `${CANVAS_HISTORY_GROUP_ID_PREFIX}${anchorNodeId}`;
}

/**
 * 从 history / history_group 投影解析绑定的根节点 id。
 * groupId 形如 `canvas-history-group:{anchorNodeId}`（D6-fix2 起按 videoId 挂历史）。
 */
export function resolveCanvasHistoryGroupAnchorNodeId(
  projection: Pick<CanvasReferenceProjectionNode, 'groupId' | 'kind' | 'id'>
): string | null {
  const groupId =
    projection.groupId ?? (projection.kind === 'history_group' ? projection.id : null);
  if (!groupId?.startsWith(CANVAS_HISTORY_GROUP_ID_PREFIX)) return null;
  const anchor = groupId.slice(CANVAS_HISTORY_GROUP_ID_PREFIX.length);
  return anchor.length > 0 ? anchor : null;
}

export interface CanvasReferenceProjectionNode {
  id: string;
  kind: CanvasReferenceProjectionNodeKind;
  target?: GenerationTargetKey;
  referenceSource?: CanvasReferenceSource;
  attachment?: CanvasAttachmentSnapshot;
  historyItem?: CanvasHistoryItem;
  historyCount?: number;
  conversation?: CanvasConversationSummary;
  groupId?: string;
  crossStoryboard?: boolean;
  available?: boolean;
  /** 仅为历史 provenance 展开而新建的 reference 投影；供 D1 可见性过滤。 */
  createdBy?: CanvasReferenceProjectionCreatedBy;
}

export type CanvasReferenceProjectionRelation =
  | 'draft'
  /** 当前定稿 provenance：生成提交清掉草稿后仍画到根卡，始终可见、不可删。 */
  | 'current'
  | 'provenance'
  /** 视频高清 / 跨卡裁剪溯源展示边（非 generation references / provenance）。 */
  | 'lineage'
  | 'history_membership'
  | 'conversation_membership';

export interface CanvasReferenceProjectionEdge {
  id: string;
  source: string;
  target: string;
  relation: CanvasReferenceProjectionRelation;
  mutable: boolean;
  reference?: GenerationReferenceItem;
  targetKey?: GenerationTargetKey;
}

export interface CanvasLogicalGroup {
  id: string;
  memberIds: string[];
}

export interface CanvasReferenceGraphProjection {
  nodes: CanvasReferenceProjectionNode[];
  edges: CanvasReferenceProjectionEdge[];
  groups: CanvasLogicalGroup[];
}

export interface ResourceIdentity {
  resourceType: GenerationResourceTypeValue;
  resourceId: string;
}

export interface ProjectCanvasReferenceGraphOptions {
  targets: CanvasGenerationTargetSnapshot[];
  drafts: GenerationReferenceDraft[];
  histories: ReadonlyMap<string, CanvasTargetHistory>;
  expandedTargetKeys: ReadonlySet<string>;
  conversations: CanvasConversationSummary[];
  attachments: CanvasAttachmentSnapshot[];
  referenceSources: CanvasReferenceSource[];
  resolveTargetNodeId: (target: GenerationTargetKey) => string | null;
  /**
   * 将 target 解析为「全部」匹配的画布节点 id（1 对多）。
   * 图片方案与其节点是 1:1，视频方案与视频节点是 1:N（一个 plan 派生多个 videoId 节点）。
   * 缺省时回退到 `resolveTargetNodeId` 的单值结果，保持既有 1:1 行为。
   */
  resolveTargetNodeIds?: (target: GenerationTargetKey) => string[];
  /**
   * draft 边专用目标解析。视频方案应落到真实 videoId 节点，且不要混入临时草稿节点。
   * 缺省回退到 `resolveTargetNodeIds` / `resolveTargetNodeId`。
   */
  resolveDraftTargetNodeIds?: (target: GenerationTargetKey) => string[];
  resolveResourceNodeId?: (identity: ResourceIdentity) => string | null;
  resolveCollapsedSourceTargetNodeId?: (identity: ResourceIdentity) => string | null;
  /**
   * 画布上已有的文件编辑溯源：sourceResourceId → 新文件。
   * 两端都在画布上才连线；缺任一端不建投影。
   */
  resourceLineagePairs?: readonly ProjectResourceLineagePair[];
}

export type ProjectResourceLineagePair = {
  resourceId: string;
  sourceResourceId: string;
  resourceType?: GenerationResourceTypeValue;
  sourceResourceType?: GenerationResourceTypeValue;
  /** 源端已在 layout 上时直连，避免身份表漏登跨分镜参考图钉。 */
  sourceNodeId?: string;
};

function readLineageResourceTypeValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isInteger(parsed)) return parsed;
  }
  return undefined;
}

function readLayoutResourceId(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * 参考图钉资源身份：列字段优先，pending 丢掉列时回退 meta.resourceId。
 * markup pin 缺类型时按附件(4)，旧分镜参考图按分镜图(2)。
 */
export function readCanvasPinResourceIdentity(item: {
  sourceResourceType?: number | string | null;
  sourceResourceId?: string | null;
  metaData?: Record<string, unknown> | null;
}): { resourceType: number; resourceId: string } | null {
  const meta = item.metaData ?? {};
  const resourceId =
    readLayoutResourceId(item.sourceResourceId) ?? readLayoutResourceId(meta.resourceId);
  if (!resourceId) return null;
  const resourceType =
    readLineageResourceTypeValue(item.sourceResourceType) ??
    readLineageResourceTypeValue(meta.sourceResourceType) ??
    (isCanvasPinAttachmentIdentity({
      sourceResourceType: item.sourceResourceType ?? meta.sourceResourceType,
      sourceKind: meta.sourceKind,
      markupPin: meta.markupPin,
    })
      ? GenerationResourceType.ATTACHMENT
      : GenerationResourceType.STORYBOARD_IMAGE);
  return { resourceType, resourceId };
}

export function readEditSourceLayoutItemId(
  metaData?: Record<string, unknown> | null
): string | null {
  return readLayoutResourceId(
    metaData?.editSourceLayoutItemId ?? metaData?.edit_source_layout_item_id
  );
}

export function resolveCanvasLayoutLineageNodeId(item: {
  itemType?: unknown;
  itemId: string;
}): string | null {
  if (isCanvasResourcePinItemType(item.itemType) || item.itemType === 15) {
    return `storyboard-reference-image:${item.itemId}`;
  }
  if (
    item.itemType === 'storyboard_item' ||
    item.itemType === 'storyboardItem' ||
    item.itemType === 2
  ) {
    return `storyboard-item-media:${item.itemId}`;
  }
  return null;
}

export function findEditSourceLayoutItem<
  TItem extends {
    itemId: string;
    sourceResourceId?: string | null;
    metaData?: Record<string, unknown> | null;
  },
>(
  layoutItems: readonly TItem[],
  params: {
    currentItemId: string;
    editSourceResourceId: string;
    editSourceLayoutItemId?: string | null;
  }
): TItem | undefined {
  const explicitId = params.editSourceLayoutItemId?.trim();
  if (explicitId) {
    const matched = layoutItems.find((item) => item.itemId === explicitId);
    if (matched) return matched;
  }
  return layoutItems.find((item) => {
    if (item.itemId === params.currentItemId) return false;
    if (item.sourceResourceId === params.editSourceResourceId) return true;
    return readLayoutResourceId(item.metaData?.resourceId) === params.editSourceResourceId;
  });
}

function lineageResourceType(value: number | undefined): GenerationResourceTypeValue {
  if (
    value === GenerationResourceType.PROJECT_RESOURCE ||
    value === GenerationResourceType.STORYBOARD_IMAGE ||
    value === GenerationResourceType.STORYBOARD_VIDEO ||
    value === GenerationResourceType.ATTACHMENT ||
    value === GenerationResourceType.SOUND_EFFECT ||
    value === GenerationResourceType.VOICE_OVER ||
    value === GenerationResourceType.HUMAN_ASSET
  ) {
    return value;
  }
  return GenerationResourceType.PROJECT_RESOURCE;
}

export function collectEditSourceLineagePairsFromCanvasItems(params: {
  resourceItems: readonly {
    itemId?: string;
    resourceId?: string | null;
    sourceResourceId?: string | null;
    editAction?: string | null;
  }[];
  layoutItems: readonly {
    itemId: string;
    itemType?: unknown;
    sourceResourceId?: string | null;
    sourceResourceType?: number | null;
    metaData?: Record<string, unknown> | null;
  }[];
}): ProjectResourceLineagePair[] {
  const resourceIdByItemId = new Map(
    params.resourceItems.flatMap((item) =>
      item.itemId && item.resourceId ? [[item.itemId, item.resourceId] as const] : []
    )
  );
  const editSourceByResourceId = new Map(
    params.resourceItems.flatMap((item) => {
      const resourceId = item.resourceId?.trim();
      const sourceResourceId = item.sourceResourceId?.trim();
      return resourceId && sourceResourceId ? [[resourceId, sourceResourceId] as const] : [];
    })
  );
  const editActionByResourceId = new Map(
    params.resourceItems.flatMap((item) => {
      const resourceId = item.resourceId?.trim();
      return resourceId && item.editAction ? [[resourceId, item.editAction] as const] : [];
    })
  );
  return collectFootageEditLineagePairs([
    ...params.layoutItems.map((layout) => {
      const resourceId = resourceIdByItemId.get(layout.itemId) ?? layout.sourceResourceId;
      const editSourceFromLayout =
        typeof layout.metaData?.editSourceResourceId === 'string'
          ? layout.metaData.editSourceResourceId
          : typeof layout.metaData?.edit_source_resource_id === 'string'
            ? layout.metaData.edit_source_resource_id
            : null;
      const editSource =
        editSourceFromLayout ?? editSourceByResourceId.get(resourceId ?? '') ?? null;
      const editSourceType = readLineageResourceTypeValue(
        layout.metaData?.editSourceResourceType ?? layout.metaData?.edit_source_resource_type
      );
      const editActionFromLayout =
        typeof layout.metaData?.editSourceEditAction === 'string'
          ? layout.metaData.editSourceEditAction
          : typeof layout.metaData?.edit_source_edit_action === 'string'
            ? layout.metaData.edit_source_edit_action
            : null;
      const sourceLayoutItem = editSource
        ? findEditSourceLayoutItem(params.layoutItems, {
            currentItemId: layout.itemId,
            editSourceResourceId: editSource,
            editSourceLayoutItemId: readEditSourceLayoutItemId(layout.metaData),
          })
        : undefined;
      const sourceNodeId = sourceLayoutItem
        ? resolveCanvasLayoutLineageNodeId(sourceLayoutItem)
        : null;
      return {
        resourceId,
        sourceResourceId: editSource,
        editAction: editActionFromLayout ?? editActionByResourceId.get(resourceId ?? '') ?? null,
        resourceType: lineageResourceType(readLineageResourceTypeValue(layout.sourceResourceType)),
        sourceResourceType: lineageResourceType(editSourceType),
        ...(sourceNodeId ? { sourceNodeId } : {}),
      };
    }),
    ...params.resourceItems.map((item) => ({
      resourceId: item.resourceId,
      sourceResourceId: item.sourceResourceId,
      editAction: item.editAction,
    })),
  ]);
}

export function collectFootageEditLineagePairs(
  items: readonly {
    id?: string | null;
    resourceId?: string | null;
    sourceResourceId?: string | null;
    editAction?: string | null;
    resourceType?: GenerationResourceTypeValue | null;
    sourceResourceType?: GenerationResourceTypeValue | null;
    sourceNodeId?: string | null;
  }[]
): ProjectResourceLineagePair[] {
  const pairs: ProjectResourceLineagePair[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    if (!isFootageMarkupLineageAction(item.editAction)) continue;
    const resourceId = item.resourceId?.trim() || item.id?.trim();
    const sourceResourceId = item.sourceResourceId?.trim();
    if (!resourceId || !sourceResourceId || resourceId === sourceResourceId) continue;
    const resourceType = lineageResourceType(item.resourceType ?? undefined);
    const sourceResourceType = lineageResourceType(item.sourceResourceType ?? undefined);
    const key = `${sourceResourceType}:${sourceResourceId}:${resourceType}:${resourceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const sourceNodeId = item.sourceNodeId?.trim();
    pairs.push({
      resourceId,
      sourceResourceId,
      resourceType,
      sourceResourceType,
      ...(sourceNodeId ? { sourceNodeId } : {}),
    });
  }
  return pairs;
}

/**
 * 标注溯源：只连画布或可见历史里已有的根，缺根不建投影。
 */
export function resolveVisibleEditLineageNodeId(params: {
  identity: ResourceIdentity;
  visibleHistoryNodeByResource?: ReadonlyMap<string, string>;
  resolveResourceNodeId?: (identity: ResourceIdentity) => string | null;
  explicitNodeId?: string | null;
}): string | null {
  return (
    params.explicitNodeId?.trim() ||
    params.resolveResourceNodeId?.(params.identity) ||
    params.visibleHistoryNodeByResource?.get(resourceKey(params.identity)) ||
    null
  );
}

export function collectVisibleProjectResourceLineageEdges(params: {
  pairs: readonly ProjectResourceLineagePair[];
  resolveResourceNodeId: (identity: ResourceIdentity) => string | null;
}): CanvasReferenceProjectionEdge[] {
  const edges: CanvasReferenceProjectionEdge[] = [];
  for (const pair of params.pairs) {
    const sourceIdentity = {
      resourceType: lineageResourceType(pair.sourceResourceType),
      resourceId: pair.sourceResourceId,
    };
    const targetIdentity = {
      resourceType: lineageResourceType(pair.resourceType),
      resourceId: pair.resourceId,
    };
    const sourceNodeId = resolveVisibleEditLineageNodeId({
      identity: sourceIdentity,
      resolveResourceNodeId: params.resolveResourceNodeId,
      explicitNodeId: pair.sourceNodeId,
    });
    const targetNodeId = params.resolveResourceNodeId(targetIdentity);
    if (!sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) continue;
    edges.push({
      id: `lineage:${resourceKey(sourceIdentity)}:${resourceKey(targetIdentity)}:${targetNodeId}`,
      source: sourceNodeId,
      target: targetNodeId,
      relation: 'lineage',
      mutable: false,
    });
  }
  return edges;
}

export function collectOutgoingReferenceTargetKeys(
  edges: readonly CanvasReferenceProjectionEdge[],
  sourceNodeId: string
): GenerationTargetKey[] {
  const seen = new Set<string>();
  const targets: GenerationTargetKey[] = [];
  for (const edge of edges) {
    if (edge.source !== sourceNodeId) continue;
    if (edge.relation !== 'draft' && edge.relation !== 'current') continue;
    if (!edge.targetKey) continue;
    const key = buildGenerationTargetCacheKey(edge.targetKey);
    if (seen.has(key)) continue;
    seen.add(key);
    targets.push(edge.targetKey);
  }
  return targets;
}

function resourceKey(identity: ResourceIdentity): string {
  return `${identity.resourceType}:${identity.resourceId}`;
}

function hasEquivalentReferenceEdge(
  edges: Iterable<CanvasReferenceProjectionEdge>,
  sourceNodeId: string,
  targetNodeId: string,
  reference: GenerationReferenceItem
): boolean {
  const identity = buildGenerationReferenceIdentity(reference);
  for (const edge of edges) {
    if (edge.source !== sourceNodeId || edge.target !== targetNodeId || !edge.reference) {
      continue;
    }
    if (buildGenerationReferenceIdentity(edge.reference) === identity) return true;
  }
  return false;
}

/**
 * 提交生成会清掉编辑态草稿边。定稿 currentResources.provenance 仍记录当时引用，
 * 必须画到当前根卡，且不可删。已有同身份草稿边时不重复；找不到画布真实源头则跳过。
 */
export function collectCurrentResourceReferenceEdges(params: {
  targets: readonly CanvasGenerationTargetSnapshot[];
  existingEdges: readonly CanvasReferenceProjectionEdge[];
  resolveTargetNodeId: (target: GenerationTargetKey) => string | null;
  resolveResourceNodeId?: (identity: ResourceIdentity) => string | null;
  resolveSourceNodeId: (reference: GenerationReferenceItem) => string | null;
}): CanvasReferenceProjectionEdge[] {
  const collected: CanvasReferenceProjectionEdge[] = [];
  const seen: CanvasReferenceProjectionEdge[] = [...params.existingEdges];

  for (const targetSnapshot of params.targets) {
    for (const resource of targetSnapshot.currentResources) {
      const provenanceReferences = resource.provenance?.references;
      if (!provenanceReferences?.length) continue;
      const targetNodeId =
        params.resolveResourceNodeId?.({
          resourceType: resource.resourceType,
          resourceId: resource.resourceId,
        }) ?? params.resolveTargetNodeId(targetSnapshot.target);
      if (!targetNodeId) continue;
      for (const reference of provenanceReferences) {
        const sourceNodeId = params.resolveSourceNodeId(reference);
        if (!sourceNodeId || sourceNodeId === targetNodeId) continue;
        if (hasEquivalentReferenceEdge(seen, sourceNodeId, targetNodeId, reference)) continue;
        const edge: CanvasReferenceProjectionEdge = {
          id: `current-ref:${resourceKey(resource)}:${targetNodeId}:${buildGenerationReferenceIdentity(reference)}`,
          source: sourceNodeId,
          target: targetNodeId,
          relation: 'current',
          mutable: false,
          reference,
          targetKey: targetSnapshot.target,
        };
        collected.push(edge);
        seen.push(edge);
      }
    }
  }
  return collected;
}

/** 去掉可能的编辑页 mention 包裹（`@类型[内部]` 或 `类型[内部]`），仅取内部名称。 */
export function stripMentionWrapper(name: string): string {
  const match = name.match(/^@?[^[\]]*\[(.+)\]$/);
  return match ? match[1] : name;
}

/**
 * 组装画布参考节点显示名 `参考图[分镜X-方案Y]`。
 *
 * 画布参考节点是纯展示投影（无输入框文本），不应沿用编辑页 mention 命名 `分镜[...]`。
 * 优先用后端结构化的来源分镜标题（X）+ 方案名（Y）；缺省时回退到剥离 mention 包裹后包成 `参考图[...]`。
 *
 * 投影节点 UI 请用 {@link buildCanvasReferenceProjectionTitle}（纯名，角标已标明类型）。
 */
export function buildCanvasReferenceDisplayName(source: CanvasReferenceSource): string {
  const planName = source.sourcePlanName?.trim();
  if (planName) {
    const storyboardPart = source.sourceStoryboardTitle?.trim();
    const inner = storyboardPart ? `${storyboardPart}-${planName}` : planName;
    return `参考图[${inner}]`;
  }
  const rawName = (source.snapshot.name ?? '').trim();
  if (!rawName) return '参考图';
  return `参考图[${stripMentionWrapper(rawName)}]`;
}

/**
 * 临时投影节点标题：纯名（strip mention 包裹），不再套 `参考图[...]`。
 * 角标已标明「临时投影」/「跨分镜参考」，标题无需再包一层类型前缀。
 * 版本口径对齐历史叶 / 引用列表：`名-标签（V{n}）`（从冻结名解析 -01 / Vn / （Vn））。
 */
export function buildCanvasReferenceProjectionTitle(source: CanvasReferenceSource): string {
  const rawName = (source.snapshot.name ?? '').trim();
  const stripped = rawName ? stripMentionWrapper(rawName) : '';
  const parsedFromSnapshot = stripped
    ? splitCanvasDisplayNameAndVersion(stripped)
    : { baseName: '', versionSeq: null as number | null };

  const planName = source.sourcePlanName?.trim();
  if (planName) {
    const storyboardPart = source.sourceStoryboardTitle?.trim();
    const structuredBase = storyboardPart ? `${storyboardPart}-${planName}` : planName;
    return (
      formatCanvasVersionedDisplayTitle(structuredBase, parsedFromSnapshot.versionSeq) || '未命名'
    );
  }

  if (!stripped) return '未命名';
  return (
    formatCanvasVersionedDisplayTitle(parsedFromSnapshot.baseName, parsedFromSnapshot.versionSeq) ||
    '未命名'
  );
}

export function buildReferenceSourceTargetMap(
  targets: CanvasGenerationTargetSnapshot[],
  sources: CanvasReferenceSource[]
): Map<string, GenerationTargetKey> {
  const targetByTagId = new Map<string, GenerationTargetKey>();
  const targetByPlanId = new Map<string, GenerationTargetKey>();
  for (const targetSnapshot of targets) {
    if (targetSnapshot.target.tagId) {
      targetByTagId.set(targetSnapshot.target.tagId, targetSnapshot.target);
    }
    if (targetSnapshot.target.planId) {
      targetByPlanId.set(targetSnapshot.target.planId, targetSnapshot.target);
    }
  }
  return new Map(
    sources.flatMap((source): Array<[string, GenerationTargetKey]> => {
      const target =
        (source.sourcePlanId ? targetByPlanId.get(source.sourcePlanId) : undefined) ??
        (source.sourceTagId ? targetByTagId.get(source.sourceTagId) : undefined);
      return target ? [[resourceKey(source), target]] : [];
    })
  );
}

/**
 * 资产画布 collapsed 策略：永远不绑主节点。
 *
 * 分镜 Stage 用 `buildReferenceSourceTargetMap(sourceTagId/sourcePlanId)` 做「换定稿绑方案」；
 * 资产侧若同样走 sourceTagId，删掉 resource_pin 后 provenance 仍会绑回标签主节点，
 * 永远走不到 `kind:reference` 投影。资产缺失源头必须出投影快照。
 */
export function resolveAssetCanvasCollapsedSourceTargetNodeId(
  _identity: ResourceIdentity
): string | null {
  return null;
}

export function getCanvasReferenceSourceNodeId(identity: ResourceIdentity): string {
  return `canvas-reference-source:${resourceKey(identity)}`;
}

export function getCanvasAttachmentNodeId(attachmentId: string): string {
  return `canvas-attachment:${attachmentId}`;
}

export function getCanvasHistoryNodeId(identity: ResourceIdentity): string {
  return `canvas-history:${resourceKey(identity)}`;
}

export function getCanvasConversationNodeId(conversationId: string): string {
  return `canvas-conversation:${conversationId}`;
}

export function getCanvasConversationHubNodeId(targetKey: string): string {
  return `canvas-conversation-hub:${targetKey}`;
}

function readSourceOriginId(resource: CanvasProjectedResource): string | null {
  const value = resource.sourceOriginId;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readSourceGenerationVideoId(resource: CanvasProjectedResource): string | null {
  const value = resource.aiParams?.source_generation_video_id;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readAiParamsString(
  aiParams: Record<string, unknown> | undefined,
  key: string
): string | null {
  const value = aiParams?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * 仅视频高清溯源：task_type=upscale，或同时具备源字段与 upscale 相关 aiParams。
 * 普通 parentId 版本链不得据此画 lineage。
 */
function isVideoUpscaleLineageCandidate(resource: CanvasProjectedResource): boolean {
  if (resource.resourceType !== GenerationResourceType.STORYBOARD_VIDEO) return false;
  const aiParams = resource.aiParams ?? {};
  if (aiParams.task_type === 'upscale') return true;
  const hasDirectSource =
    (typeof resource.sourceId === 'string' && resource.sourceId.length > 0) ||
    Boolean(readAiParamsString(aiParams, 'source_video_id'));
  const hasUpscaleRelatedAiParams = Boolean(
    readAiParamsString(aiParams, 'source_generation_video_id')
  );
  return hasDirectSource && hasUpscaleRelatedAiParams;
}

/**
 * 高清直接源：sourceId → source_video_id → parentId（仅 upscale 候选）。
 */
function resolveUpscaleDirectSourceVideoId(resource: CanvasProjectedResource): string | null {
  if (typeof resource.sourceId === 'string' && resource.sourceId.length > 0) {
    return resource.sourceId;
  }
  const sourceVideoId = readAiParamsString(resource.aiParams, 'source_video_id');
  if (sourceVideoId) return sourceVideoId;
  if (
    isVideoUpscaleLineageCandidate(resource) &&
    typeof resource.parentId === 'string' &&
    resource.parentId.length > 0
  ) {
    return resource.parentId;
  }
  return null;
}

export function resolveVideoLineageRootId(
  resource: CanvasProjectedResource,
  historyItems: readonly CanvasHistoryItem[] = []
): string {
  const byId = new Map<string, CanvasProjectedResource>(
    historyItems.map((item) => [item.resource.resourceId, item.resource])
  );
  byId.set(resource.resourceId, resource);
  let current: CanvasProjectedResource | undefined = resource;
  const seen = new Set<string>();
  while (current) {
    if (seen.has(current.resourceId)) break;
    seen.add(current.resourceId);
    const nextId =
      readSourceGenerationVideoId(current) ?? current.parentId ?? current.sourceId ?? null;
    if (!nextId || nextId === current.resourceId) {
      return current.parentId ?? current.resourceId;
    }
    const next = byId.get(nextId);
    if (!next) return current.parentId ?? nextId;
    current = next;
  }
  return resource.parentId ?? resource.resourceId;
}

export function areVideosInSameLineage(
  left: CanvasProjectedResource,
  right: CanvasProjectedResource,
  historyItems: readonly CanvasHistoryItem[] = []
): boolean {
  if (left.resourceId === right.resourceId) return true;
  if (left.resourceType !== GenerationResourceType.STORYBOARD_VIDEO) return false;
  if (right.resourceType !== GenerationResourceType.STORYBOARD_VIDEO) return false;
  return (
    resolveVideoLineageRootId(left, historyItems) === resolveVideoLineageRootId(right, historyItems)
  );
}

function buildFallbackSource(reference: GenerationReferenceItem): CanvasReferenceSource {
  return {
    resourceType: reference.resourceType,
    resourceId: reference.resourceId,
    snapshot: reference.snapshot,
    available: false,
    crossStoryboard: false,
  };
}

export function projectCanvasReferenceGraph({
  targets,
  drafts,
  histories,
  expandedTargetKeys,
  conversations,
  attachments,
  referenceSources,
  resolveTargetNodeId,
  resolveTargetNodeIds,
  resolveDraftTargetNodeIds,
  resolveResourceNodeId,
  resolveCollapsedSourceTargetNodeId,
  resourceLineagePairs,
}: ProjectCanvasReferenceGraphOptions): CanvasReferenceGraphProjection {
  const resolveTargetNodeIdList = (target: GenerationTargetKey): string[] => {
    if (resolveTargetNodeIds) return resolveTargetNodeIds(target);
    const single = resolveTargetNodeId(target);
    return single ? [single] : [];
  };
  const resolveDraftTargetNodeIdList = (target: GenerationTargetKey): string[] => {
    if (resolveDraftTargetNodeIds) return resolveDraftTargetNodeIds(target);
    return resolveTargetNodeIdList(target);
  };
  const nodeById = new Map<string, CanvasReferenceProjectionNode>();
  const edgeById = new Map<string, CanvasReferenceProjectionEdge>();
  const groupById = new Map<string, CanvasLogicalGroup>();
  const sourceByIdentity = new Map(referenceSources.map((source) => [resourceKey(source), source]));
  const attachmentById = new Map(
    attachments.map((attachment) => [attachment.attachmentId, attachment])
  );
  const visibleHistoryNodeByResource = new Map<string, string>();

  const ensureNode = (node: CanvasReferenceProjectionNode) => {
    const existing = nodeById.get(node.id);
    if (!existing) {
      nodeById.set(node.id, node);
      return node.id;
    }
    // provenance 后写 target / createdBy：若节点已由另一路径创建且缺字段，补写以便 flow 落位与 D1 过滤。
    if (existing.kind === 'reference' && node.kind === 'reference') {
      const nextTarget = node.target && !existing.target ? node.target : existing.target;
      const nextCreatedBy =
        node.createdBy && !existing.createdBy ? node.createdBy : existing.createdBy;
      if (nextTarget !== existing.target || nextCreatedBy !== existing.createdBy) {
        nodeById.set(node.id, {
          ...existing,
          ...(nextTarget ? { target: nextTarget } : {}),
          ...(nextCreatedBy ? { createdBy: nextCreatedBy } : {}),
        });
      }
    }
    return node.id;
  };
  const ensureEdge = (edge: CanvasReferenceProjectionEdge) => {
    if (!edgeById.has(edge.id)) edgeById.set(edge.id, edge);
  };

  for (const attachment of attachments) {
    ensureNode({
      id: getCanvasAttachmentNodeId(attachment.attachmentId),
      kind: 'attachment',
      attachment,
      available: true,
    });
  }

  /**
   * 展开历史绑定：优先按真实画布节点 id（视频=videoId 节点）匹配 histories。
   * 支持 versions 链挂到定稿视频节点，避免仍用 planId 幽灵节点导致边不可见。
   */
  const expandedHistoryBindings: Array<{
    targetNodeId: string;
    target: GenerationTargetKey;
    history: CanvasTargetHistory;
    currentResources: CanvasGenerationTargetSnapshot['currentResources'];
  }> = [];
  const boundHistoryNodeIds = new Set<string>();
  const targetSnapshotByCacheKey = new Map(
    targets.map((snapshot) => [buildGenerationTargetCacheKey(snapshot.target), snapshot] as const)
  );

  for (const targetSnapshot of targets) {
    const candidateNodeIds = Array.from(
      new Set([
        ...resolveTargetNodeIdList(targetSnapshot.target),
        ...(resolveTargetNodeId(targetSnapshot.target)
          ? [resolveTargetNodeId(targetSnapshot.target)!]
          : []),
      ])
    );
    for (const targetNodeId of candidateNodeIds) {
      if (!expandedTargetKeys.has(targetNodeId) || boundHistoryNodeIds.has(targetNodeId)) {
        continue;
      }
      const history = histories.get(targetNodeId);
      if (!history || history.items.length === 0) continue;
      boundHistoryNodeIds.add(targetNodeId);
      expandedHistoryBindings.push({
        targetNodeId,
        target: targetSnapshot.target,
        history,
        currentResources: targetSnapshot.currentResources,
      });
    }
  }

  for (const [targetNodeId, history] of histories.entries()) {
    if (!expandedTargetKeys.has(targetNodeId) || boundHistoryNodeIds.has(targetNodeId)) {
      continue;
    }
    if (!history || history.items.length === 0) continue;
    const matchedSnapshot = targetSnapshotByCacheKey.get(
      buildGenerationTargetCacheKey(history.target)
    );
    boundHistoryNodeIds.add(targetNodeId);
    expandedHistoryBindings.push({
      targetNodeId,
      target: history.target,
      history,
      currentResources: matchedSnapshot?.currentResources ?? [],
    });
  }

  for (const binding of expandedHistoryBindings) {
    const { targetNodeId, target, history } = binding;
    // API 默认新→旧；画布组内固定为旧→新（先生成在上）。
    const historicalItems = [...history.items].sort((left, right) => {
      const leftTime = Date.parse(left.resource.createdAt ?? '') || 0;
      const rightTime = Date.parse(right.resource.createdAt ?? '') || 0;
      if (leftTime !== rightTime) return leftTime - rightTime;
      const leftVersion = left.resource.version ?? 0;
      const rightVersion = right.resource.version ?? 0;
      if (leftVersion !== rightVersion) return leftVersion - rightVersion;
      return left.resource.resourceId.localeCompare(right.resource.resourceId);
    });
    const groupId = buildCanvasHistoryGroupId(targetNodeId);
    const memberIds = [groupId];
    ensureNode({
      id: groupId,
      kind: 'history_group',
      target,
      historyCount: historicalItems.length,
      groupId,
      available: true,
    });
    // D1-history-group-edge + 左列：history_group → 根（对齐 conversation_membership：hub→root）
    // 虚线从历史组右侧连到根左侧；一条边覆盖整组，不连每个历史叶子。
    ensureEdge({
      id: `history-membership:${groupId}:${targetNodeId}`,
      source: groupId,
      target: targetNodeId,
      relation: 'history_membership',
      mutable: false,
      targetKey: target,
    });
    for (const historyItem of historicalItems) {
      const historyNodeId = getCanvasHistoryNodeId(historyItem.resource);
      visibleHistoryNodeByResource.set(resourceKey(historyItem.resource), historyNodeId);
      memberIds.push(historyNodeId);
      ensureNode({
        id: historyNodeId,
        kind: 'history',
        target,
        historyItem,
        groupId,
        available: true,
      });
    }
    groupById.set(groupId, { id: groupId, memberIds });
  }

  /**
   * 解析引用边源头节点。
   * - draft：只绑 attachment / history leaf / 可见 resource / collapsed；禁止建投影，找不到则返回 null（跳过该边）
   * - provenance：缺失时仍 ensureNode(kind:'reference')（含 crossStoryboard）；须写入 target 供 flow 相对锚点落位
   */
  const resolveReferenceEndpoint = (
    reference: GenerationReferenceItem,
    {
      allowMissingProjection,
      projectionTarget,
    }: {
      allowMissingProjection: boolean;
      /** provenance 所属 GenerationTargetKey；创建 kind:reference 时写入，避免默认 x=-360 */
      projectionTarget?: GenerationTargetKey;
    }
  ): string | null => {
    const identity: ResourceIdentity = {
      resourceType: reference.resourceType,
      resourceId: reference.resourceId,
    };
    const identityKey = resourceKey(identity);
    const source = sourceByIdentity.get(identityKey) ?? buildFallbackSource(reference);

    if (
      reference.resourceType === GenerationResourceType.ATTACHMENT &&
      attachmentById.has(reference.resourceId)
    ) {
      return getCanvasAttachmentNodeId(reference.resourceId);
    }
    const exactHistoryNodeId = visibleHistoryNodeByResource.get(identityKey);
    if (exactHistoryNodeId) return exactHistoryNodeId;
    const visibleResourceNodeId = resolveResourceNodeId?.(identity);
    if (visibleResourceNodeId) return visibleResourceNodeId;
    if (source.crossStoryboard) {
      if (!allowMissingProjection) return null;
      return ensureNode({
        id: getCanvasReferenceSourceNodeId(identity),
        kind: 'reference',
        target: projectionTarget,
        referenceSource: source,
        crossStoryboard: true,
        available: source.available,
        // D1：仅 provenance 路径允许建投影；标记供选中门控隐藏。
        createdBy: CANVAS_HISTORY_PROVENANCE_CREATED_BY,
      });
    }
    const collapsedTargetNodeId = resolveCollapsedSourceTargetNodeId?.(identity);
    if (collapsedTargetNodeId) return collapsedTargetNodeId;
    if (!allowMissingProjection) return null;
    return ensureNode({
      id: getCanvasReferenceSourceNodeId(identity),
      kind: 'reference',
      target: projectionTarget,
      referenceSource: source,
      crossStoryboard: false,
      available: source.available,
      // D1：仅 provenance 路径允许建投影；标记供选中门控隐藏。
      createdBy: CANVAS_HISTORY_PROVENANCE_CREATED_BY,
    });
  };

  // 视频方案 1:N：draft 边默认也会展开；D1-per-video-draft 后应传入
  // resolveDraftTargetNodeIds，使 planId=videoId|draftId 时严格 1:1。
  for (const draft of drafts) {
    const targetNodeIds = resolveDraftTargetNodeIdList(draft.target);
    if (targetNodeIds.length === 0) continue;
    for (const targetNodeId of targetNodeIds) {
      for (const reference of draft.references) {
        // D1-draft-no-projection：草稿边不得以投影为源；找不到真实源头则跳过。
        const sourceNodeId = resolveReferenceEndpoint(reference, {
          allowMissingProjection: false,
        });
        if (!sourceNodeId) continue;
        ensureEdge({
          id: `draft:${buildGenerationTargetCacheKey(
            draft.target
          )}:${targetNodeId}:${buildGenerationReferenceIdentity(reference)}`,
          source: sourceNodeId,
          target: targetNodeId,
          relation: 'draft',
          mutable: true,
          reference,
          targetKey: draft.target,
        });
      }
    }
  }

  for (const edge of collectCurrentResourceReferenceEdges({
    targets,
    existingEdges: Array.from(edgeById.values()),
    resolveTargetNodeId,
    resolveResourceNodeId,
    resolveSourceNodeId: (reference) =>
      resolveReferenceEndpoint(reference, { allowMissingProjection: false }),
  })) {
    ensureEdge(edge);
  }

  const resolveLineageSourceNodeId = (originVideoId: string): string | null => {
    const identity: ResourceIdentity = {
      resourceType: GenerationResourceType.STORYBOARD_VIDEO,
      resourceId: originVideoId,
    };
    // 跨卡边优先挂源卡外层；源卡历史展开时不能改挂到它的历史叶。
    const resourceNodeId = resolveResourceNodeId?.(identity);
    if (resourceNodeId) return resourceNodeId;
    const collapsedNodeId = resolveCollapsedSourceTargetNodeId?.(identity);
    if (collapsedNodeId) return collapsedNodeId;
    return visibleHistoryNodeByResource.get(resourceKey(identity)) ?? null;
  };

  const resolveCurrentVideoNodeId = (
    resource: CanvasProjectedResource,
    target: GenerationTargetKey
  ): string | null =>
    resolveResourceNodeId?.({
      resourceType: resource.resourceType,
      resourceId: resource.resourceId,
    }) ?? resolveTargetNodeId(target);

  // 跨卡裁剪 / 高清(裁剪)：current 外层用 sourceOriginId 连到最近跨卡源，不依赖展开历史。
  for (const targetSnapshot of targets) {
    for (const resource of targetSnapshot.currentResources) {
      if (resource.resourceType !== GenerationResourceType.STORYBOARD_VIDEO) continue;
      const originVideoId = readSourceOriginId(resource);
      if (!originVideoId || originVideoId === resource.resourceId) continue;
      const targetNodeId = resolveCurrentVideoNodeId(resource, targetSnapshot.target);
      const sourceRootVideoId =
        typeof resource.sourceRootVideoId === 'string' && resource.sourceRootVideoId.length > 0
          ? resource.sourceRootVideoId
          : null;
      const sourceNodeId =
        resolveLineageSourceNodeId(originVideoId) ??
        (sourceRootVideoId && sourceRootVideoId !== resource.resourceId
          ? resolveLineageSourceNodeId(sourceRootVideoId)
          : null);
      if (!targetNodeId || !sourceNodeId || sourceNodeId === targetNodeId) continue;
      ensureEdge({
        id: `lineage:${resourceKey(resource)}:${resourceKey({
          resourceType: GenerationResourceType.STORYBOARD_VIDEO,
          resourceId: originVideoId,
        })}:${targetNodeId}`,
        source: sourceNodeId,
        target: targetNodeId,
        relation: 'lineage',
        mutable: false,
        targetKey: targetSnapshot.target,
      });
    }
  }

  for (const binding of expandedHistoryBindings) {
    const { target, history } = binding;
    for (const historyItem of history.items) {
      const historyNodeId = visibleHistoryNodeByResource.get(resourceKey(historyItem.resource));
      if (!historyNodeId) continue;
      // 对齐分镜图片：展开后 provenance 一律挂历史叶子；禁止因「高清同链」改挂定稿主节点。
      for (const reference of historyItem.references) {
        const sourceNodeId = resolveReferenceEndpoint(reference, {
          allowMissingProjection: true,
          projectionTarget: target,
        });
        if (!sourceNodeId) continue;
        ensureEdge({
          id: `provenance:${resourceKey(
            historyItem.resource
          )}:${buildGenerationReferenceIdentity(reference)}`,
          source: sourceNodeId,
          target: historyNodeId,
          relation: 'provenance',
          mutable: false,
          reference,
          targetKey: target,
        });
      }
    }
  }

  // D6-upscale-provenance-edge（方案 2）：仅视频历史叶子按 upscale lineage 画溯源展示边。
  // 禁止伪造 generation references / 新建 kind:reference；源必须已在可见 history 叶子中。
  for (const binding of expandedHistoryBindings) {
    const { target, history } = binding;
    for (const historyItem of history.items) {
      const upscaledResource = historyItem.resource;
      if (!isVideoUpscaleLineageCandidate(upscaledResource)) continue;
      const sourceVideoId = resolveUpscaleDirectSourceVideoId(upscaledResource);
      if (!sourceVideoId || sourceVideoId === upscaledResource.resourceId) continue;
      const targetResourceKey = resourceKey(upscaledResource);
      const sourceResourceKey = resourceKey({
        resourceType: GenerationResourceType.STORYBOARD_VIDEO,
        resourceId: sourceVideoId,
      });
      const sourceHistoryNodeId = visibleHistoryNodeByResource.get(sourceResourceKey);
      const targetHistoryNodeId = visibleHistoryNodeByResource.get(targetResourceKey);
      if (!sourceHistoryNodeId || !targetHistoryNodeId) continue;
      if (sourceHistoryNodeId === targetHistoryNodeId) continue;
      ensureEdge({
        id: `lineage:${targetResourceKey}:${sourceResourceKey}`,
        source: sourceHistoryNodeId,
        target: targetHistoryNodeId,
        relation: 'lineage',
        mutable: false,
        targetKey: target,
      });
    }
  }

  // 展开历史后：历史叶若有跨卡 sourceOriginId，连到源卡可见节点（不造 reference 投影）。
  for (const binding of expandedHistoryBindings) {
    const { target, history } = binding;
    for (const historyItem of history.items) {
      const resource = historyItem.resource;
      if (resource.resourceType !== GenerationResourceType.STORYBOARD_VIDEO) continue;
      const originVideoId = readSourceOriginId(resource);
      if (!originVideoId || originVideoId === resource.resourceId) continue;
      const targetHistoryNodeId = visibleHistoryNodeByResource.get(resourceKey(resource));
      const sourceNodeId = resolveLineageSourceNodeId(originVideoId);
      if (!targetHistoryNodeId || !sourceNodeId || sourceNodeId === targetHistoryNodeId) continue;
      ensureEdge({
        id: `lineage:${resourceKey(resource)}:${resourceKey({
          resourceType: GenerationResourceType.STORYBOARD_VIDEO,
          resourceId: originVideoId,
        })}:${targetHistoryNodeId}`,
        source: sourceNodeId,
        target: targetHistoryNodeId,
        relation: 'lineage',
        mutable: false,
        targetKey: target,
      });
    }
  }

  // D7-membership-source-to-root：hub 为源、根节点为终点；edgeId=`conversation-membership:{hub}:{root}`。
  const buildConversationMembershipEdgeId = (hubNodeId: string, rootNodeId: string) =>
    `conversation-membership:${hubNodeId}:${rootNodeId}`;
  for (const conversation of conversations) {
    // 已绑定会话即展示枢纽（含 messageCount=0 的空会话）；按 target(含 planId) 区分。
    // 视频方案 1:N：一个 plan 派生多个视频节点，需各连一条虚线到同一会话枢纽。
    const rootNodeIds = resolveTargetNodeIdList(conversation.target);
    if (rootNodeIds.length === 0) continue;
    const targetKey = buildGenerationTargetCacheKey(conversation.target);
    const hubNodeId = getCanvasConversationHubNodeId(targetKey);
    const existing = nodeById.get(hubNodeId);
    if (existing?.kind === 'conversation' && existing.conversation) {
      const existingTime = new Date(existing.conversation.lastMessageAt ?? 0).getTime();
      const nextTime = new Date(conversation.lastMessageAt ?? 0).getTime();
      if (nextTime <= existingTime) continue;
      // 同一 target 只保留一颗会话枢纽节点，面板内再切换具体会话；
      // 替换时清掉旧枢纽及其全部成员边（可能有多条）。
      nodeById.delete(existing.id);
      for (const edgeId of Array.from(edgeById.keys())) {
        if (edgeId.startsWith(`conversation-membership:${hubNodeId}:`)) {
          edgeById.delete(edgeId);
        }
      }
    }
    const conversationNodeId = ensureNode({
      id: hubNodeId,
      kind: 'conversation',
      target: conversation.target,
      conversation,
      available: true,
    });
    for (const rootNodeId of rootNodeIds) {
      ensureEdge({
        id: buildConversationMembershipEdgeId(conversationNodeId, rootNodeId),
        source: conversationNodeId,
        target: rootNodeId,
        relation: 'conversation_membership',
        mutable: false,
        targetKey: conversation.target,
      });
    }
  }

  if (resourceLineagePairs?.length) {
    for (const edge of collectVisibleProjectResourceLineageEdges({
      pairs: resourceLineagePairs,
      resolveResourceNodeId: (identity) =>
        resolveVisibleEditLineageNodeId({
          identity,
          visibleHistoryNodeByResource,
          resolveResourceNodeId,
        }),
    })) {
      ensureEdge(edge);
    }
  }

  return {
    nodes: Array.from(nodeById.values()),
    edges: Array.from(edgeById.values()),
    groups: Array.from(groupById.values()),
  };
}

/**
 * 若选中/拖动的节点命中逻辑组任一成员，则扩到该组全部 memberIds。
 * 用于多选拖拽落盘：RF dragItems 可能只有被框到的子节点，组框与兄弟需一并保存。
 */
export function expandNodeIdsWithLogicalGroups(
  nodeIds: readonly string[],
  groups: readonly CanvasLogicalGroup[]
): string[] {
  const seed = new Set(nodeIds);
  if (seed.size === 0) return [];
  const expanded = new Set(seed);
  for (const group of groups) {
    if (!group.memberIds.some((id) => seed.has(id))) continue;
    for (const memberId of group.memberIds) {
      expanded.add(memberId);
    }
  }
  return Array.from(expanded);
}

export function moveCanvasLogicalGroup<
  TNode extends { id: string; position: { x: number; y: number } },
>({
  nodes,
  groups,
  draggedNodeId,
  delta,
}: {
  nodes: TNode[];
  groups: CanvasLogicalGroup[];
  draggedNodeId: string;
  delta: { x: number; y: number };
}): TNode[] {
  const group = groups.find((candidate) => candidate.memberIds.includes(draggedNodeId));
  if (!group) {
    return nodes.map((node) =>
      node.id === draggedNodeId
        ? {
            ...node,
            position: {
              x: node.position.x + delta.x,
              y: node.position.y + delta.y,
            },
          }
        : node
    );
  }
  const memberIds = new Set(group.memberIds);
  return nodes.map((node) =>
    memberIds.has(node.id)
      ? {
          ...node,
          position: {
            x: node.position.x + delta.x,
            y: node.position.y + delta.y,
          },
        }
      : node
  );
}
